import { SuggestionDisclosure } from './suggestion-disclosure.tsx';
import { useEffect, useRef, useState } from 'react';
import { useMutation, useMutationState, useQuery, useQueryClient, type QueryKey } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { inboxKindActionsApi, type EditableInboxKind } from '../api/inbox-kind-actions.ts';
import { useInboxKindScope } from '../state/inbox-kind-scope.ts';
import { captureInboxPreviewLease, useInboxPreviewFence } from '../state/inbox-preview-lease.ts';
import { applyInboxActionReceipt } from '../state/inbox-action-receipts.ts';
import { useAiDisclosure } from './ai-disclosure.tsx';
import { Alert, Button, Select, TextArea } from './ui.tsx';
import { ActionCard } from './inbox-action-suggestions.tsx';
import type { InboxActionReceipt, InboxActionSuggestionAny } from '../../shared/inbox-actions.ts';
import { formatDateAndTime, formatLocalTime, formatNumericDate } from '../i18n/datetime.ts';
import { usePendingSuggestions } from '../hooks/use-pending-suggestions.ts';
import { useSuggestionDisclosureMemory, type DisclosureMemory } from '../state/suggestion-disclosure-state.ts';

export function InboxKindSuggestions({ conversationId, kind }: { conversationId: string; kind: EditableInboxKind }) {
  const scope = useInboxKindScope(`${kind}:${conversationId}`);
  const memory = useSuggestionDisclosureMemory(scope.disclosureIdentity, `${kind}:${conversationId}`);
  if (!scope.canRead(kind)) return null;
  return (
    <KindSuggestions
      key={scope.identity}
      conversationId={conversationId}
      kind={kind}
      identity={scope.identity}
      memory={memory}
      scopeCurrent={scope.current}
      receiptScopeCurrent={scope.receiptCurrent}
      canDecide={scope.canDecide(kind)}
    />
  );
}

function KindSuggestions({
  conversationId,
  kind,
  identity,
  scopeCurrent,
  receiptScopeCurrent,
  canDecide,
  memory,
}: {
  conversationId: string;
  kind: EditableInboxKind;
  identity: string;
  scopeCurrent: () => boolean;
  receiptScopeCurrent: () => boolean;
  canDecide: boolean;
  memory: DisclosureMemory;
}) {
  const { t } = useTranslation();
  const client = useQueryClient();
  const disclosure = useAiDisclosure();
  const active = useRef(true);
  useEffect(() => {
    active.current = true;
    return () => {
      active.current = false;
    };
  }, []);
  const current = () => active.current && scopeCurrent();
  const key = ['inbox-kind-actions', kind, conversationId, identity];
  const context = useQuery({
    queryKey: key,
    queryFn: () => inboxKindActionsApi.context(conversationId, kind),
    retry: false,
  });
  const previewCurrent = useInboxPreviewFence(client, key, context.data, current);
  const [appointmentId, selectVisit] = useState('');
  const selection = useRef({ appointmentId, source: context.data?.sourceMessageId });
  selection.current = { appointmentId, source: context.data?.sourceMessageId };
  const request = useRef<{ key: string; id: string } | null>(null);
  const [empty, setEmpty] = useState(false);
  const [error, setError] = useState(false);
  type Input = {
    id: string;
    sourceMessageId: string;
    appointmentId: string;
    lease: ReturnType<typeof captureInboxPreviewLease>;
  };
  const generation = useMutation({
    mutationFn: async (input: Input) => {
      const eligible = () =>
        current() &&
        previewCurrent() &&
        input.lease.current() &&
        selection.current.source === input.sourceMessageId &&
        (kind === 'create_quote' || selection.current.appointmentId === input.appointmentId);
      try {
        await disclosure.ensure(true, eligible);
        if (!eligible()) throw new Error('stale');
        const value = await inboxKindActionsApi.suggest(
          conversationId,
          kind === 'create_quote'
            ? { id: input.id, kind, sourceMessageId: input.sourceMessageId }
            : { id: input.id, kind, sourceMessageId: input.sourceMessageId, appointmentId: input.appointmentId },
        );
        return { value, visible: eligible() };
      } finally {
        input.lease.dispose();
      }
    },
    onSuccess: async ({ value, visible }) => {
      if (!visible || !current()) return;
      request.current = null;
      setEmpty(!value.suggestion);
      setError(false);
      await client.invalidateQueries({ queryKey: key });
    },
    onError: (caught) => {
      if (current() && caught.message !== 'stale') setError(true);
    },
  });
  const data = context.data;
  const attentionKey = usePendingSuggestions(data?.suggestions);
  const decisions = useMutationState({
    filters: { mutationKey: ['inbox-kind-decision', key], exact: true },
    select: (mutation) => ({
      id: mutation.options.meta?.suggestionId,
      sequence: mutation.mutationId,
      status: mutation.state.status,
    }),
  });
  // A retry creates a new cache entry. Only the latest attempt for each
  // still-pending proposal can describe its current review status.
  const latest = new Map<unknown, (typeof decisions)[number]>();
  for (const item of decisions) {
    if (!data?.suggestions.some((row) => row.id === item.id && row.status === 'pending')) continue;
    if (item.sequence > (latest.get(item.id)?.sequence ?? -1)) latest.set(item.id, item);
  }
  const deciding = [...latest.values()].some((item) => item.status === 'pending');
  const decisionFailed = [...latest.values()].some((item) => item.status === 'error');
  return (
    <SuggestionDisclosure
      title={t(`inboxKind.${kind}.title`)}
      memory={memory}
      attentionKey={attentionKey}
      busy={context.isLoading || generation.isPending || deciding}
      failed={Boolean(context.error || error || decisionFailed)}
      status={deciding ? t('saveIndicator.saving') : decisionFailed ? t('inboxKind.checkStatus') : undefined}
    >
      {disclosure.dialog}
      {context.error ? (
        <Alert tone="error">
          {t('inboxKind.loadError')}
          <Button variant="secondary" onClick={() => void context.refetch()}>
            {t('common.retry')}
          </Button>
        </Alert>
      ) : !data ? (
        <p role="status">{t('common.loading')}</p>
      ) : (
        <>
          <p>{t(`inboxKind.${kind}.review`)}</p>
          {canDecide && data.sourceMessageId && (kind === 'create_quote' || data.visits.length > 0) ? (
            <>
              {kind === 'visit_instruction' ? (
                <Select
                  label={t('inboxKind.visit')}
                  value={appointmentId}
                  onChange={(event) => {
                    selectVisit(event.target.value);
                    request.current = null;
                  }}
                  options={[
                    { value: '', label: t('inboxAction.choose') },
                    ...data.visits.map((visit) => ({
                      value: visit.id,
                      label: `${formatDateAndTime(visit.scheduled_date, visit.start_time_local)} (${visit.timezone})`,
                    })),
                  ]}
                />
              ) : null}
              <Button
                variant="secondary"
                busy={generation.isPending}
                disabled={
                  generation.isPending || context.isFetching || (kind === 'visit_instruction' && !appointmentId)
                }
                onClick={() => {
                  const lease = captureInboxPreviewLease(client, key, data, () => current() && previewCurrent());
                  if (!lease.current()) {
                    lease.dispose();
                    return;
                  }
                  if (kind === 'visit_instruction' && !data.visits.some((visit) => visit.id === appointmentId)) {
                    lease.dispose();
                    return;
                  }
                  const sourceMessageId = data.sourceMessageId!;
                  const inputKey = JSON.stringify([
                    kind,
                    sourceMessageId,
                    kind === 'visit_instruction' ? appointmentId : null,
                  ]);
                  if (request.current?.key !== inputKey) request.current = { key: inputKey, id: crypto.randomUUID() };
                  setError(false);
                  setEmpty(false);
                  generation.mutate({ id: request.current.id, sourceMessageId, appointmentId, lease });
                }}
              >
                {t(`inboxKind.${kind}.suggest`)}
              </Button>
            </>
          ) : null}
          {empty ? <p>{t('inboxAction.none')}</p> : null}
          {error ? <Alert tone="error">{t('inboxKind.generateError')}</Alert> : null}
          {data.suggestions.map((suggestion) => (
            <InboxKindActionCard
              key={`${suggestion.id}:${JSON.stringify(suggestion)}`}
              suggestion={suggestion}
              canDecide={
                canDecide &&
                !generation.isPending &&
                !context.isFetching &&
                (kind === 'create_quote' || data.visits.some((visit) => visit.id === suggestion.appointmentId))
              }
              queryKey={key}
              queryData={data}
              scopeCurrent={current}
              receiptScopeCurrent={receiptScopeCurrent}
              refresh={() => context.refetch()}
            />
          ))}
        </>
      )}
    </SuggestionDisclosure>
  );
}

export function InboxKindActionCard({
  suggestion,
  canDecide,
  queryKey,
  queryData,
  scopeCurrent,
  receiptScopeCurrent,
  refresh,
  token,
}: {
  suggestion: InboxActionSuggestionAny;
  canDecide: boolean;
  queryKey: QueryKey;
  queryData: unknown;
  scopeCurrent: () => boolean;
  receiptScopeCurrent: () => boolean;
  refresh: () => Promise<unknown>;
  token?: string;
}) {
  const { t } = useTranslation();
  const client = useQueryClient();
  const navigate = useNavigate();
  const active = useRef(true);
  useEffect(() => {
    active.current = true;
    return () => {
      active.current = false;
    };
  }, []);
  const kind = suggestion.proposal.kind;
  const original =
    suggestion.proposal.kind === 'visit_instruction'
      ? suggestion.proposal.text
      : suggestion.proposal.kind === 'create_quote'
        ? suggestion.proposal.internalNotes
        : '';
  const [edited, setEdited] = useState(original);
  const attempted = useRef<{ value: 'approve' | 'dismiss'; text: string; data: unknown } | null>(null);
  const [retryVerified, setRetryVerified] = useState(false);
  const previewCurrent = useInboxPreviewFence(client, queryKey, queryData, scopeCurrent, (denied) => {
    setEdited(original);
    setRetryVerified(false);
    if (denied) attempted.current = null;
  });
  const current = () => active.current && scopeCurrent() && previewCurrent();
  const edit = useRef({ edited });
  if (edit.current.edited !== edited) edit.current = { edited };
  const capturedEdit = edit.current;
  const [clock, setClock] = useState(Date.now);
  const expires = Date.parse(suggestion.expiresAt);
  useEffect(() => {
    const timer = setTimeout(() => setClock(Date.now()), Math.min(2_147_483_647, Math.max(0, expires - Date.now())));
    return () => clearTimeout(timer);
  }, [expires]);
  const receipts = useRef(new Set<string>());
  const record = async (receipt: InboxActionReceipt) => {
    const identity = JSON.stringify(receipt);
    if (!receiptScopeCurrent() || receipts.current.has(identity)) return;
    receipts.current.add(identity);
    await applyInboxActionReceipt(client, receipt, receiptScopeCurrent);
  };
  const receipt = 'receipt' in suggestion ? suggestion.receipt : undefined;
  useEffect(() => {
    if (suggestion.status === 'approved' && receipt) void record(receipt);
  }, [suggestion.status, receipt]);
  const decision = useMutation({
    mutationKey: ['inbox-kind-decision', queryKey],
    meta: { suggestionId: suggestion.id },
    mutationFn: async ({
      value,
      text,
      lease,
    }: {
      value: 'approve' | 'dismiss';
      text: string;
      lease: ReturnType<typeof captureInboxPreviewLease>;
    }) => {
      try {
        if (!current() || !lease.current() || expires <= Date.now()) throw new Error('stale');
        const result = await inboxKindActionsApi.decide(suggestion, {
          decision: value,
          ...(token ? { token } : {}),
          ...(value === 'approve' && kind === 'visit_instruction' ? { editedText: text } : {}),
          ...(value === 'approve' && kind === 'create_quote' ? { editedNotes: text } : {}),
        });
        return { value: result, visible: current() && lease.current() };
      } finally {
        lease.dispose();
      }
    },
    onSuccess: async ({ value, visible }) => {
      if (!receiptScopeCurrent()) return;
      if (value.receipt) await record(value.receipt);
      if (kind === 'reschedule')
        await Promise.all([
          client.invalidateQueries({ queryKey: ['schedule'] }),
          client.invalidateQueries({ queryKey: ['appointments'] }),
          client.invalidateQueries({ queryKey: ['appointment', suggestion.appointmentId] }),
          client.invalidateQueries({ queryKey: ['series'] }),
        ]);
      if (visible && current()) await refresh();
    },
  });
  const checkStatus = async () => {
    const frozen = attempted.current;
    const previousUpdates = client.getQueryState(queryKey)?.dataUpdateCount ?? 0;
    setRetryVerified(false);
    const result = await refresh();
    // Only a successful authoritative read of the identical pending preview permits an explicit retry.
    const state = client.getQueryState(queryKey);
    if (
      frozen &&
      attempted.current === frozen &&
      active.current &&
      scopeCurrent() &&
      navigator.onLine !== false &&
      expires > Date.now() &&
      state &&
      result &&
      typeof result === 'object' &&
      'isSuccess' in result &&
      result.isSuccess === true &&
      state.dataUpdateCount > previousUpdates &&
      !state.error &&
      !state.isInvalidated &&
      state.fetchStatus === 'idle' &&
      JSON.stringify(state.data) === JSON.stringify(frozen.data)
    )
      setRetryVerified(true);
  };
  const retryDecision = () => {
    const frozen = attempted.current;
    if (!retryVerified || !frozen || !canDecide || decision.isPending || !current()) return;
    const lease = captureInboxPreviewLease(client, queryKey, queryData, current);
    if (!lease.current()) {
      lease.dispose();
      return;
    }
    setRetryVerified(false);
    decision.mutate({ value: frozen.value, text: frozen.text, lease });
  };
  const decide = (value: 'approve' | 'dismiss') => {
    if (edit.current !== capturedEdit) return;
    if (!canDecide || decision.isPending || decision.error || suggestion.status !== 'pending') return;
    const text = edited.trim();
    if (
      value === 'approve' &&
      kind !== 'reschedule' &&
      (!text || text.length > (kind === 'visit_instruction' ? 5000 : 8000))
    )
      return;
    const lease = captureInboxPreviewLease(client, queryKey, queryData, current);
    if (!lease.current()) {
      lease.dispose();
      return;
    }
    attempted.current = { value, text, data: queryData };
    setRetryVerified(false);
    decision.mutate({ value, text, lease });
  };
  if (kind === 'reschedule')
    return (
      <>
        <ActionCard
          suggestion={suggestion as Extract<InboxActionSuggestionAny, { proposal: { kind: 'reschedule' } }>}
          canDecide={canDecide && !decision.isPending && !decision.error}
          onDecide={decide}
        />
        {decision.error ? (
          <Alert tone="error">
            {t('inboxKind.decisionError')}{' '}
            <Button variant="secondary" onClick={() => void checkStatus()}>
              {t('inboxKind.checkStatus')}
            </Button>
            {retryVerified ? (
              <Button variant="secondary" onClick={retryDecision}>
                {t('inboxKind.retryDecision')}
              </Button>
            ) : null}
          </Alert>
        ) : null}
      </>
    );
  const pending = suggestion.status === 'pending' && expires > clock;
  return (
    <article className="mt-3 min-w-0 rounded border p-3">
      <h4 className="font-semibold break-words">{suggestion.target.customerName}</h4>
      {kind === 'visit_instruction' ? (
        <p>
          {t('inboxAction.original', {
            date: suggestion.target.scheduledDate ? formatNumericDate(suggestion.target.scheduledDate) : '',
            time: suggestion.target.startTimeLocal ? formatLocalTime(suggestion.target.startTimeLocal) : '',
            timezone: suggestion.target.timezone,
          })}
        </p>
      ) : null}
      <p className="break-words whitespace-pre-wrap">{suggestion.proposal.summary}</p>
      {pending && canDecide ? (
        <TextArea
          label={t(`inboxKind.${kind}.edit`)}
          value={edited}
          rows={4}
          maxLength={kind === 'visit_instruction' ? 5000 : 8000}
          disabled={decision.isPending || Boolean(decision.error)}
          onChange={(event) => setEdited(event.target.value)}
        />
      ) : (
        <p className="break-words whitespace-pre-wrap">{original}</p>
      )}
      <p>{t(`inboxAction.${suggestion.status}`)}</p>
      {pending && canDecide ? (
        <div className="mt-2 flex flex-wrap gap-2">
          <Button
            busy={decision.isPending}
            disabled={!edited.trim() || decision.isPending || Boolean(decision.error)}
            onClick={() => decide('approve')}
          >
            {t(`inboxKind.${kind}.approve`)}
          </Button>
          <Button
            variant="secondary"
            disabled={decision.isPending || Boolean(decision.error)}
            onClick={() => decide('dismiss')}
          >
            {t('inboxAction.dismiss')}
          </Button>
        </div>
      ) : null}
      {decision.error ? (
        <Alert tone="error">
          {t('inboxKind.decisionError')}{' '}
          <Button variant="secondary" onClick={() => void checkStatus()}>
            {t('inboxKind.checkStatus')}
          </Button>
          {retryVerified && attempted.current ? (
            <>
              <p className="break-words whitespace-pre-wrap">{attempted.current.text}</p>
              <Button variant="secondary" onClick={retryDecision}>
                {t('inboxKind.retryDecision')}
              </Button>
            </>
          ) : null}
        </Alert>
      ) : null}
      {suggestion.status === 'approved' && receipt?.kind === 'create_quote' ? (
        <Button
          variant="secondary"
          onClick={() => {
            const lease = captureInboxPreviewLease(client, queryKey, queryData, current);
            const allowed = lease.current();
            lease.dispose();
            if (allowed) navigate(`/quotes/${encodeURIComponent(receipt.quoteId)}`);
          }}
        >
          {t('inboxKind.create_quote.open')}
        </Button>
      ) : null}
    </article>
  );
}
